import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { accentAlt, brand, neutral, semantic } from "../../../theme/tokens";
import dayjs from "dayjs";
import { Drawer, Descriptions, Tag, Typography, Button, Space, Spin, Empty, message } from "antd";
import {
    EditOutlined,
    KeyOutlined,
    CrownOutlined,
    UserOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import { staffAPIWithErrorHandling } from "../../../api/staff/staff";

const { Text } = Typography;

const ROLE_CONFIG = {
    BRANCH_MANAGER: { text: "Quản lý", icon: <CrownOutlined style={{ color: semantic.warningSolid }} /> },
    WARD_LEADER: { text: "Quản lý cấp phường", icon: <CrownOutlined style={{ color: accentAlt.purpleSolid }} /> },
    STAFF: { text: "Nhân viên", icon: <UserOutlined style={{ color: brand.primary }} /> },
};

/**
 * Drawer xem chi tiết nhân sự.
 *
 * Chỉ một khối thông tin ngắn, không bảng con nên không đáng một trang riêng.
 * Tự gọi getStaffById vì drawer này còn được mở từ cột "Quản lý" của màn đơn vị,
 * nơi chỉ có mỗi id và tên.
 *
 * @param {boolean} open
 * @param {number|string|null} staffId
 * @param {function} onClose
 * @param {function} [onEdit]  Có thì hiện nút "Chỉnh sửa", nhận vào nhân sự vừa tải.
 */
export default function StaffDetailDrawer({ open, staffId, onClose, onEdit }) {
    const navigate = useNavigate();
    const [staff, setStaff] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !staffId) return;

        let ignore = false;

        const fetchStaff = async () => {
            setLoading(true);
            try {
                const response = await staffAPIWithErrorHandling.getStaffById(staffId);
                const data = response.data?.data || response.data;
                if (!ignore) setStaff(data);
            } catch (error) {
                if (!ignore) {
                    setStaff(null);
                    message.error(error.message || "Không thể tải thông tin nhân sự");
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        };

        fetchStaff();

        // Đóng rồi mở bản ghi khác nhanh -> bỏ kết quả của lần gọi cũ.
        return () => {
            ignore = true;
        };
    }, [open, staffId]);

    const handleClose = () => {
        setStaff(null);
        onClose?.();
    };

    const role = ROLE_CONFIG[staff?.user?.info?.type] || {
        text: "Không xác định",
        icon: <UserOutlined style={{ color: neutral[400] }} />,
    };

    // Nhân sự phụ trách được nhiều đơn vị; `staff_org_map` là dạng cũ một đơn vị.
    const orgNames = staff?.organizations?.length
        ? staff.organizations.map((item) => item.name)
        : [staff?.staff_org_map?.organization?.name].filter(Boolean);

    return (
        <Drawer
            title="Chi tiết nhân sự"
            width="min(560px, 100vw)"
            open={open}
            onClose={handleClose}
            destroyOnHidden
            footer={
                <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={handleClose}>Đóng</Button>
                    {staff?.user_id && (
                        <Button
                            icon={<KeyOutlined />}
                            onClick={() => navigate(`/add-permission/${staff.user_id}`)}
                        >
                            Cấp quyền
                        </Button>
                    )}
                    {onEdit && staff && (
                        <Button type="primary" icon={<EditOutlined />} onClick={() => onEdit(staff)}>
                            Chỉnh sửa
                        </Button>
                    )}
                </Space>
            }
        >
            <Spin spinning={loading}>
                {!staff && !loading ? (
                    <Empty description="Không tìm thấy thông tin nhân sự" />
                ) : (
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="Mã nhân sự">
                            <Text strong>{staff?.code || "-"}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Họ và tên">{staff?.name || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Số điện thoại">
                            {staff?.phone ? <a href={`tel:${staff.phone}`}>{staff.phone}</a> : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Email">
                            {staff?.email ? <a href={`mailto:${staff.email}`}>{staff.email}</a> : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ngày sinh">
                            {staff?.dob ? dayjs(staff.dob).format("DD/MM/YYYY") : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Địa chỉ">
                            {staff?.address ? (
                                <span style={{ whiteSpace: "pre-wrap" }}>{staff.address}</span>
                            ) : (
                                "-"
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Đơn vị">
                            {orgNames.length ? (
                                <Space size={[4, 4]} wrap>
                                    {orgNames.map((name) => (
                                        <Tag color="geekblue" key={name}>
                                            {name}
                                        </Tag>
                                    ))}
                                </Space>
                            ) : (
                                "-"
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Vai trò">
                            <Space size={6}>
                                {role.icon}
                                {role.text}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="Trạng thái tài khoản">
                            <Tag
                                icon={staff?.status ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                color={staff?.status ? "success" : "error"}
                            >
                                {staff?.status ? "Đang hoạt động" : "Không hoạt động"}
                            </Tag>
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Spin>
        </Drawer>
    );
}
